/**
 * SCRUM-158 (P-8) — Zod schemas for the PropertyBlocking HTTP boundary.
 *
 * Endpoints covered (spec §10.1):
 *   #25 POST   /organizations/:slug/properties/:propertyId/property-blockings   CreatePropertyBlocking
 *   #26 DELETE .../property-blockings/:blockingId                               RemovePropertyBlocking (path-param only)
 *   #27 GET    /organizations/:slug/properties/:propertyId/property-blockings   ListPropertyBlockings
 *
 * `conflictingReservations` is always `[]` in Phase 4 Lite (NOOP `ReservationConflictPort`).
 * The schema still validates the shape so the API contract is forward-compatible.
 *
 * Adversarial tests: packages/validators/src/__tests__/pricing/property-blocking.spec.ts
 */
import { z } from 'zod';
import { isoLocalDateString, instantString } from './_shared.js';
// ---------------------------------------------------------------------------
// CreatePropertyBlockingRequestSchema — endpoint #25
// ---------------------------------------------------------------------------
/**
 * POST /organizations/:slug/properties/:propertyId/property-blockings body.
 * Semi-open date range: dateFrom < dateTo (dateTo is exclusive).
 */
export const CreatePropertyBlockingRequestSchema = z
    .object({
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
    reason: z.string().min(1).max(500),
})
    .refine((req) => req.dateFrom < req.dateTo, {
    message: 'dateFrom must be before dateTo (semi-open range)',
    path: ['dateTo'],
});
// ---------------------------------------------------------------------------
// Response schemas — endpoints #25, #27
// ---------------------------------------------------------------------------
/**
 * ReservationSummarySchema — embedded in CreatePropertyBlockingResponseSchema.
 * Populated by ReservationConflictPort (NOOP in Lite → always []).
 */
export const ReservationSummarySchema = z.object({
    reservationId: z.string().uuid(),
    guestName: z.string(),
    arrivalDate: isoLocalDateString,
    departureDate: isoLocalDateString,
});
/**
 * CreatePropertyBlockingResponseSchema — POST response (endpoint #25).
 */
export const CreatePropertyBlockingResponseSchema = z.object({
    propertyBlockingId: z.string().uuid(),
    conflictingReservations: z.array(ReservationSummarySchema),
});
/**
 * PropertyBlockingItemSchema — one entry in the list response.
 */
export const PropertyBlockingItemSchema = z.object({
    id: z.string().uuid(),
    propertyId: z.string().uuid(),
    dateFrom: isoLocalDateString,
    dateTo: isoLocalDateString,
    reason: z.string(),
    createdAt: instantString,
});
/**
 * PropertyBlockingListResponseSchema — GET .../property-blockings response (endpoint #27).
 */
export const PropertyBlockingListResponseSchema = z.object({
    items: z.array(PropertyBlockingItemSchema),
});
//# sourceMappingURL=property-blocking.js.map